import axios from "axios";
import { clearUser } from "../store/auth/userSlice"; // Assuming you have this action to clear user data

const handleUnauthorized = (dispatch, navigate) => {
	dispatch(clearUser());
	navigate("/login");
};

export const fetchRoles = async (
	token,
	dispatch,
	navigate,
	page,
	perPage,
	paginate = 1,
	searchTerms,
	signal,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			page: page,
			per_page: perPage,
			paginate: paginate,
			search: searchTerms?.search,
		},
		signal,
	};
	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/roles`, config);
		let data;

		if (!response.data.data || response.data.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		} else {
			const totalItems = response.data.total;
			data = {
				data: response.data.data,
				totalItems: totalItems,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		}
		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching roles data:", error);
		}
	}
};

export const createRole = async (data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};

	try {
		const response = await axios.post(`${import.meta.env.VITE_API_BACKEND}api/roles`, data, config);
		return { status: response.status, data: response.data };
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 422) {
			return { status: error.response.status, data: error.response.data };
		} else {
			console.error("Error creating role permissions:", error);
		}
		return null;
	}
};

export const updateRole = async (id, data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	try {
		const response = await axios.put(
			`${import.meta.env.VITE_API_BACKEND}api/roles/${id}`,
			data,
			config,
		);
		return { status: response.status, data: response.data };
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 422) {
			return { status: error.response.status, data: error.response.data };
		} else {
			console.error("Error updating role permissions:", error);
		}
		return null;
	}
};

export const deleteRoleById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.delete(
			`${import.meta.env.VITE_API_BACKEND}api/roles/${id}`,
			config,
		);
		if (response.status === 200 || response.status === 202 || response.status === 204) {
			return {
				status: "success",
				message: "Role deleted successfully.",
			};
		} else {
			return {
				status: "error",
				message: response.data?.message || "An unexpected error occurred.",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 404:
					return { status: "not_found", message: "Not found." };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				case 400:
					return {
						status: "error",
						message: error.response.data?.message || "An error occurred.",
					};
				default:
					return { status: "error", message: "An error occurred." };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};
export const fetchAllTheme = async (token, dispatch, navigate, paginate = 0) => {
	if (!token) {
		console.error("No token provided, cannot fetch theme.");
		return;
	}

	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			paginate: paginate,
		},
	};

	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/themes`, config);

		return response.data.data || [];
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching theme data:", error);
		}
	}
};

export const fetchRoleswithPermissions = async (token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			parent: 1,
		},
	};
	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/roles`, config);
		const data = response.data || [];
		return data || [];
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching permissions by role:", error);
		}
	}
};

export const fetchRoleDetails = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/roles/${id}`, config);
		const data = response.data[0] || [];
		return data || [];
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching permissions by role:", error);
		}
	}
};

export const fetchAllRolesData = async (token, dispatch, navigate, paginate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			paginate: paginate,
		},
	};
	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/roles`, config);
		let data;

		if (!response.data || response.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
			};
		} else {
			const totalItems = response.data.total;
			data = {
				data: response.data,
				totalItems: totalItems,
			};
		}
		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching all roles data:", error);
		}
	}
};
